import { useEffect, useState } from 'react'
import { supabase } from '../../api/supabase'
import type { Invoice } from '../../hooks/useInvoices'
import { INVOICE_CATEGORIES } from '../../lib/invoiceCategory'
import { canEditAmounts } from '../../lib/invoiceRules'
import { fmt } from '../../lib/invoiceFormat'
import InvoiceDialogShell, { ErrorBox, btnCancel, fieldCls, labelCls } from './InvoiceDialogShell'

interface EditLine { id: string; description: string; sale_quantity: number | null; sale_rate: number | null; sale_amount: number | null; sale_vat_rate: number | null; remove: boolean }
interface OpenCharge { id: string; description: string; sale_amount: number | null }

// Two kinds of edit (Gary's rules, 2026-10-01):
//  - free: invoice date (not once synced to Sage), due date, category -- no code
//  - money: line qty/rate/amount, VAT rate, Bill To, add/remove lines -- authorisation code + reason,
//    checked on the server (edit_invoice_amounts); the code is asked for BEFORE the editor unlocks.
export default function EditInvoiceDialog({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const [invoiceDate, setInvoiceDate] = useState(invoice.invoice_date?.slice(0, 10) || '')
  const [dueDate, setDueDate] = useState(invoice.due_date?.slice(0, 10) || '')
  const [category, setCategory] = useState(invoice.invoice_category || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Money editor state
  const [askAuth, setAskAuth] = useState(false)
  const [code, setCode] = useState('')
  const [authReason, setAuthReason] = useState('')
  const [unlocked, setUnlocked] = useState(false)
  const [lines, setLines] = useState<EditLine[]>([])
  const [openCharges, setOpenCharges] = useState<OpenCharge[]>([])
  const [addIds, setAddIds] = useState<Set<string>>(new Set())
  const [billTo, setBillTo] = useState<{ id: string; name: string }>({ id: invoice.customer_id, name: invoice.customer?.name || '' })
  const [custQuery, setCustQuery] = useState('')
  const [custResults, setCustResults] = useState<{ id: string; name: string }[]>([])

  const amountsAllowed = canEditAmounts(invoice)
  const dateLocked = !!invoice.sage_invoice_id

  useEffect(() => {
    if (!unlocked) return
    ;(async () => {
      const { data } = await supabase.from('job_charges').select('id, description, sale_quantity, sale_rate, sale_amount, sale_vat_rate, charge_code:charge_codes(description)').eq('invoice_id', invoice.id)
      setLines((data || []).map((c: any) => ({ id: c.id, description: c.charge_code?.description || c.description || '(charge)', sale_quantity: c.sale_quantity, sale_rate: c.sale_rate, sale_amount: c.sale_amount, sale_vat_rate: c.sale_vat_rate, remove: false })))
      if (invoice.job_id) {
        const { data: open } = await supabase.from('job_charges').select('id, description, sale_amount, charge_code:charge_codes(description)').eq('job_id', invoice.job_id).is('invoice_id', null).not('sale_amount', 'is', null)
        setOpenCharges((open || []).map((c: any) => ({ id: c.id, description: c.charge_code?.description || c.description || '(charge)', sale_amount: c.sale_amount })))
      }
    })()
  }, [unlocked, invoice.id, invoice.job_id])

  useEffect(() => {
    if (!unlocked || custQuery.trim().length < 2) { setCustResults([]); return }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('customers').select('id, name').ilike('name', `%${custQuery.trim()}%`).order('name').limit(10)
      setCustResults(data || [])
    }, 250)
    return () => clearTimeout(t)
  }, [custQuery, unlocked])

  const saveFree = async () => {
    setBusy(true); setError(null)
    const { error } = await supabase.rpc('edit_invoice_free_fields', { p_invoice_id: invoice.id, p_invoice_date: invoiceDate || null, p_due_date: dueDate || null, p_category: category || null })
    setBusy(false)
    if (error) { setError(error.message); return }
    onDone()
  }

  const verifyAndUnlock = async () => {
    setBusy(true); setError(null)
    const { data: ok, error } = await supabase.rpc('verify_invoice_edit_auth_code', { p_code: code })
    setBusy(false)
    if (error) { setError(error.message); return }
    if (!ok) { setError('Incorrect authorisation code (or none has been set yet — Admin > Company Settings).'); return }
    setAskAuth(false); setUnlocked(true)
  }

  const setLine = (id: string, patch: Partial<EditLine>) => setLines(prev => prev.map(l => {
    if (l.id !== id) return l
    const next = { ...l, ...patch }
    // Keep amount = qty × rate when either of those is edited; amount itself stays directly editable.
    if (('sale_quantity' in patch || 'sale_rate' in patch) && next.sale_quantity != null && next.sale_rate != null) {
      next.sale_amount = Math.round(next.sale_quantity * next.sale_rate * 100) / 100
    }
    return next
  }))

  const saveAmounts = async () => {
    setBusy(true); setError(null)
    const changes = {
      lines: lines.filter(l => !l.remove).map(l => ({ charge_id: l.id, sale_quantity: l.sale_quantity, sale_rate: l.sale_rate, sale_amount: l.sale_amount, sale_vat_rate: l.sale_vat_rate })),
      remove_charge_ids: lines.filter(l => l.remove).map(l => l.id),
      add_charge_ids: Array.from(addIds),
      bill_to_customer_id: billTo.id,
    }
    const { error } = await supabase.rpc('edit_invoice_amounts', { p_invoice_id: invoice.id, p_auth_code: code, p_reason: authReason, p_changes: changes })
    setBusy(false)
    if (error) { setError(error.message); return }
    onDone()
  }

  const num = (v: string) => (v === '' ? null : parseFloat(v))
  const cell = 'w-20 px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white text-xs text-right'

  return (
    <>
      <InvoiceDialogShell title={`Edit invoice ${invoice.invoice_no}`} width="max-w-3xl"
        footer={<button onClick={onClose} className={btnCancel}>Close</button>}>
        <section className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Dates & category <span className="font-normal text-xs text-gray-500">— no code needed</span></h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Invoice date</label>
              <input type="date" value={invoiceDate} disabled={dateLocked} onChange={e => setInvoiceDate(e.target.value)} className={fieldCls} />
              {dateLocked && <p className="text-[11px] text-gray-500 mt-1">Locked: synced to Sage.</p>}
            </div>
            <div>
              <label className={labelCls}>Due date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className={fieldCls}>
                <option value="">—</option>
                {INVOICE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Changing the category does not renumber the invoice (no "D" is added or removed).</p>
          <button onClick={saveFree} disabled={busy} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Save dates & category</button>
        </section>

        <hr className="border-gray-200 dark:border-gray-700" />

        <section className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Amounts, VAT rate, Bill To, charge lines <span className="font-normal text-xs text-amber-700 dark:text-amber-400">— authorisation code required</span></h4>
          {!amountsAllowed && (
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              This invoice has been synced to Sage — its amounts can only be corrected with a Credit Note.
            </p>
          )}
          {amountsAllowed && !unlocked && (
            <button onClick={() => { setError(null); setAskAuth(true) }} className="px-4 py-2 text-sm border border-amber-400 text-amber-800 dark:text-amber-300 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20">
              Edit amounts…
            </button>
          )}
          {unlocked && (
            <>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-gray-500 dark:text-gray-400 uppercase"><th>Charge</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th><th className="text-right">VAT %</th><th /></tr></thead>
                <tbody>
                  {lines.map(l => (
                    <tr key={l.id} className={`border-t border-gray-100 dark:border-gray-700 ${l.remove ? 'opacity-40 line-through' : ''}`}>
                      <td className="py-1 pr-2 text-gray-800 dark:text-gray-200">{l.description}</td>
                      <td className="text-right"><input type="number" step="any" className={cell} disabled={l.remove} value={l.sale_quantity ?? ''} onChange={e => setLine(l.id, { sale_quantity: num(e.target.value) })} /></td>
                      <td className="text-right"><input type="number" step="any" className={cell} disabled={l.remove} value={l.sale_rate ?? ''} onChange={e => setLine(l.id, { sale_rate: num(e.target.value) })} /></td>
                      <td className="text-right"><input type="number" step="0.01" className={cell} disabled={l.remove} value={l.sale_amount ?? ''} onChange={e => setLine(l.id, { sale_amount: num(e.target.value) })} /></td>
                      <td className="text-right"><input type="number" step="any" className={cell} disabled={l.remove} value={l.sale_vat_rate ?? ''} onChange={e => setLine(l.id, { sale_vat_rate: num(e.target.value) })} /></td>
                      <td className="pl-2"><button onClick={() => setLine(l.id, { remove: !l.remove })} className="text-red-600 hover:underline">{l.remove ? 'Undo' : 'Remove'}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {openCharges.length > 0 && (
                <div>
                  <label className={labelCls}>Add un-invoiced charges from this job</label>
                  <div className="space-y-1">
                    {openCharges.map(c => (
                      <label key={c.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                        <input type="checkbox" checked={addIds.has(c.id)} onChange={() => setAddIds(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n })} />
                        {c.description} — {fmt(c.sale_amount || 0, invoice.currency)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className={labelCls}>Bill To: <b className="text-gray-800 dark:text-gray-200">{billTo.name}</b></label>
                <input value={custQuery} onChange={e => setCustQuery(e.target.value)} placeholder="Search customer to change Bill To…" className={fieldCls} />
                {custResults.length > 0 && (
                  <ul className="border border-gray-200 dark:border-gray-700 rounded mt-1 text-sm">
                    {custResults.map(c => <li key={c.id}><button onClick={() => { setBillTo(c); setCustQuery(''); setCustResults([]) }} className="w-full text-left px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200">{c.name}</button></li>)}
                  </ul>
                )}
              </div>
              <button onClick={saveAmounts} disabled={busy} className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save amount changes'}
              </button>
            </>
          )}
          <ErrorBox message={!askAuth ? error : null} />
        </section>
      </InvoiceDialogShell>

      {askAuth && (
        <InvoiceDialogShell title="⚠️ Change the amounts on an issued invoice" tone="warn" width="max-w-sm"
          footer={<>
            <button onClick={() => setAskAuth(false)} className={btnCancel}>Cancel</button>
            <button onClick={verifyAndUnlock} disabled={!code || !authReason.trim() || busy} className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {busy ? 'Checking…' : 'Confirm'}
            </button>
          </>}>
          <p className="text-sm text-gray-700 dark:text-gray-300">Invoice <b>{invoice.invoice_no}</b> · {fmt(invoice.total_amount || 0, invoice.currency)}<br />Changing receivable amounts needs the authorisation code.</p>
          <div><label className={labelCls}>Authorisation code</label><input type="password" autoComplete="off" value={code} onChange={e => setCode(e.target.value)} className={fieldCls} /></div>
          <div><label className={labelCls}>Reason for change *</label><input value={authReason} onChange={e => setAuthReason(e.target.value)} className={fieldCls} /></div>
          <ErrorBox message={error} />
        </InvoiceDialogShell>
      )}
    </>
  )
}
